import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import EmblaCarousel from "@tCentro/Components/EmblaCarousel";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import filter from "lodash/filter";
import sortBy from "lodash/sortBy";
import truncate from "lodash/truncate";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// const enum fields {
//     defaultPageSize = "defaultPageSize",
//     title = "title",
// }

interface OwnProps extends WidgetProps {
    fields: {
        defaultPageSize: number;
        title: string;
        linkURL: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const newsAndListPageUrl = getPageLinkByPageType(state, "NewsListPage")?.type as string;
    const nodeId: string = state.links.pageTypesToNodeId[newsAndListPageUrl];
    const pages = getDataView(state.data.pages, { parentNodeId: nodeId });
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }
    return {
        parentNodeId: nodeId,
        location: getLocation(state),
        pages,
        links,
        parameter: state.pages.newsList.getNewsPagesParameters[nodeId] ?? {
            parentNodeId: nodeId,
        },
        viewAll: getPageLinkByPageType(state, "NewsListPage")?.url,
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    newsParentWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    announcementLink?: LinkPresentationProps;
    browseAllLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    wrapper?: InjectableCss;
    announcementTitle?: InjectableCss;
    header?: TextFieldPresentationProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    MobileView?: InjectableCss;
    articleLinkStyles?: InjectableCss;
}

export const newsListViewStyles: NewsListViewStyles = {
    articleLinkStyles: {
        css: css`
            > span {
                color: ${getProp("theme.typography.p.color")};
                &:hover {
                    color: ${getColor("text.link")} !important;
                }
            }
        `,
    },
    titleText: {
        size: 20,
        css: css`
            margin-bottom: 0;
        `,
    },
    newsParentWrapper: {
        css: css`
            padding-right: 24px;
            flex: 1;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: 100%;
                        `,
                        css`
                            height: 100%;
                        `,
                        css`
                            height: 100%;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    newsWrapper: {
        css: css`
            padding: 15px;
            box-shadow:
                0px 0px 1px rgba(38, 37, 37, 0.56),
                0px 2px 8px rgba(38, 37, 37, 0.16);
            background-color: ${COLORS.white};
            height: 100%;
            .news-wrapper {
                display: flex;
            }
            .news-content {
                width: 70%;
                padding-left: 10px;
                h3 {
                    font-size: ${TYPOGRAPHY.size.smaller};
                    font-weight: ${TYPOGRAPHY.weight.base};
                    a {
                        & :focus {
                            outline: none;
                        }
                        span {
                            color: ${getProp("theme.typography.p.color")};
                        }
                    }
                }

                h5 {
                    font-size: ${TYPOGRAPHY.size.tiny};
                    color: #0b70b7;
                    margin-bottom: 5px;
                }

                p {
                    display: none;
                }
            }

            .newz-img {
                width: 5.625rem;
                height: 60px;
                .img-wrapper {
                    img {
                        height: inherit;
                    }
                }
            }
        `,
    },
    announcementLink: {
        css: css`
            p {
                color: ${getProp("theme.typography.p.color")};
            }
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                border-top: 1px solid ${getColor("common.border")};
                padding-top: 15px;
            `,
        },
    },
    summaryWrapper: {
        css: css`
            margin-top: 8px;
            line-height: 20px;

            p {
                font-size: ${TYPOGRAPHY.size.smaller};
                font-weight: ${TYPOGRAPHY.weight.base};
                text-transform: capitalize;
            }
        `,
    },
    authorText: {
        css: css`
            font-weight: bold;
            margin-right: 8px;
        `,
    },
    dateAndAuthorWrapper: {
        css: css`
            line-height: 16px;
        `,
    },
    publishDateText: {
        css: css`
            font-size: ${TYPOGRAPHY.size.tiny};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-style: normal;
            color: ${COLORS.primary.base};
        `,
    },
    listWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-direction: column;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    wrapper: {},
    MobileView: {
        css: css`
            display: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: inline-block;
                        font-size: ${TYPOGRAPHY.size.tiny};
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        line-height: 16px;
                        color: ${COLORS.link.base};
                    `,
                    css`
                        display: inline-block;
                        font-size: ${TYPOGRAPHY.size.tiny};
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        line-height: 16px;
                        color: ${COLORS.link.base};
                    `,
                    css`
                        display: inline-block;
                        font-size: ${TYPOGRAPHY.size.tiny};
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        line-height: 16px;
                        color: ${COLORS.link.base};
                    `,
                    null,
                ])}
        `,
    },

    announcementTitle: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-bottom: 15px;
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
        `,
    },
    browseAllLink: {
        css: css`
            display: inline-block;
            font-size: ${TYPOGRAPHY.size.tiny};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            margin-top: 20px;
            color: ${COLORS.link.base};
            margin-bottom: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            position: relative;
            padding-bottom: 33px;

            > div:last-child {
                bottom: -2px;

                button {
                    padding: 0 8px !important;
                }
            }
        `,
    },
    carouselSlide: {
        css: css`
            flex: 0 0 85%;
            padding: 0;
            position: relative;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            text-align: left;
            position: relative;
            padding: 0 3px 0;
        `,
    },
};

const styles = newsListViewStyles;

class NewsListView extends React.Component<Props> {
    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount() {
        if (!this.props.pages.value && this.props.parentNodeId && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    componentDidUpdate() {
        if (!this.props.pages.value && this.props.parentNodeId && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    changePage = (newPageIndex: number) => {
        this.updateQueryString(newPageIndex, this.props.parameter.pageSize);
    };

    changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        this.updateQueryString(1, newPageSize);
    };

    updateQueryString(page: number, pageSize?: number) {
        const {
            history,
            location: { pathname },
        } = this.props;
        let newLocation = `${pathname}?page=${page}`;
        if (pageSize) {
            newLocation += `&pageSize=${pageSize}`;
        }
        history.push(newLocation);
    }

    checkQueryString() {
        const {
            location: { search },
            parameter: { page, pageSize, parentNodeId },
        } = this.props;
        const parsedQueryString = parseQueryString<{ page?: string; pageSize?: string }>(search);
        const newPage = parsedQueryString.page ? convertToNumberIfString(parsedQueryString.page) : undefined;
        const newPageSize = parsedQueryString.pageSize
            ? convertToNumberIfString(parsedQueryString.pageSize)
            : undefined;
        if (newPage !== page || newPageSize !== pageSize) {
            this.props.updateLoadParameter({
                page: newPage,
                pageSize: newPageSize,
                parentNodeId,
            });
        }
    }

    render() {
        const {
            links,
            pages: { value: pages },
            fields: { defaultPageSize, linkURL },
            parameter,
            viewAll,
        } = this.props;

        if (!pages) {
            return null;
        }

        const page = parameter.page ?? 1;
        const pageSize = parameter.pageSize ?? defaultPageSize;

        const sortedAndPagedPages = sortBy(pages, o => o.fields["publishDate"]).reverse();

        const filteredPages = sortedAndPagedPages
            .filter((page: PageProps) => {
                return page.fields["articleType"];
            })
            .slice((page - 1) * pageSize, page * pageSize);

        if (filteredPages?.length === 0) {
            return null;
        }

        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.announcementTitle}>
                    {translate("Latest Announcements")}
                    <Link href={linkURL} {...styles.MobileView}>
                        {translate("Browse All Articles")}
                    </Link>
                </StyledWrapper>
                <Hidden below="lg">
                    <StyledWrapper {...styles.listWrapper} className="detailList">
                        {filteredPages.slice(0, defaultPageSize).map((page: PageProps) => (
                            <StyledWrapper {...styles.newsParentWrapper} key={page.id}>
                                <StyledWrapper {...styles.newsWrapper} key={page.id} className="newsWrapper">
                                    <StyledWrapper>
                                        <Link href={links[page.nodeId]?.url} {...styles.articleLinkStyles}>
                                            {page.fields["title"]}
                                        </Link>
                                    </StyledWrapper>
                                    {/* <StyledWrapper {...styles.dateAndAuthorWrapper}>
                                        {page.fields["publishDate"] && (
                                            <Typography {...styles.publishDateText}>
                                                <LocalizedDateTime
                                                    dateTime={new Date(page.fields["publishDate"])}
                                                    options={{ year: "numeric", month: "long", day: "numeric" }}
                                                />
                                            </Typography>
                                        )}
                                    </StyledWrapper>
                                    {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                                        <StyledWrapper {...styles.summaryWrapper}>
                                            <Link href={links[page.nodeId]?.url} {...styles.announcementLink}>
                                                {parse(
                                                    page.fields["newsSummary"] ||
                                                        truncate(page.fields["newsContent"], { length: 500 }),
                                                    parserOptions,
                                                )}
                                            </Link>
                                        </StyledWrapper>
                                    )} */}
                                </StyledWrapper>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </Hidden>
                <Link href={linkURL} {...styles.browseAllLink}>
                    {translate("Browse All Articles")}
                </Link>

                <Hidden above="md">
                    <StyledWrapper {...styles.carouselWrapper}>
                        <StyledWrapper {...styles.carouselContainer}>
                            <StyledWrapper {...styles.carouselSlidesContainer}>
                                <EmblaCarousel showDots={true} showArrow={false}>
                                    {sortedAndPagedPages.slice(0, defaultPageSize).map((page: PageProps, index) => (
                                        <>
                                            {index < defaultPageSize && (
                                                <StyledWrapper {...styles.carouselSlide} key={page.id}>
                                                    <StyledWrapper {...styles.carouselSlideInner}>
                                                        <StyledWrapper {...styles.newsParentWrapper} key={page.id}>
                                                            <StyledWrapper
                                                                {...styles.newsWrapper}
                                                                key={page.id}
                                                                className="newsWrapper"
                                                            >
                                                                <StyledWrapper>
                                                                    <Link
                                                                        href={links[page.nodeId]?.url}
                                                                        {...styles.articleLinkStyles}
                                                                    >
                                                                        {page.fields["title"]}
                                                                    </Link>
                                                                </StyledWrapper>
                                                                {/* <StyledWrapper {...styles.dateAndAuthorWrapper}>
                                                                    {page.fields["publishDate"] && (
                                                                        <Typography {...styles.publishDateText}>
                                                                            <LocalizedDateTime
                                                                                dateTime={
                                                                                    new Date(page.fields["publishDate"])
                                                                                }
                                                                                options={{
                                                                                    year: "numeric",
                                                                                    month: "long",
                                                                                    day: "numeric",
                                                                                }}
                                                                            />
                                                                        </Typography>
                                                                    )}
                                                                </StyledWrapper>
                                                                {(page.fields["newsSummary"] ||
                                                                    page.fields["newsContent"]) && (
                                                                    <StyledWrapper {...styles.summaryWrapper}>
                                                                        <Link
                                                                            href={links[page.nodeId]?.url}
                                                                            {...styles.announcementLink}
                                                                        >
                                                                            {parse(
                                                                                page.fields["newsSummary"] ||
                                                                                    truncate(
                                                                                        page.fields["newsContent"],
                                                                                        { length: 500 },
                                                                                    ),
                                                                                parserOptions,
                                                                            )}
                                                                        </Link>
                                                                    </StyledWrapper>
                                                                )} */}
                                                            </StyledWrapper>
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            )}
                                        </>
                                    ))}
                                </EmblaCarousel>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </Hidden>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(NewsListView)),
    definition: {
        group: "News List",
        fieldDefinitions: [
            {
                displayName: "CEN Latest Announcements",
                name: "defaultPageSize",
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "Translatable",
                min: 1,
                max: 5,
            },
            {
                fieldType: "Translatable",
                name: "linkURL",
                editorTemplate: "TextField",
                displayName: "Link URL",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
